import {
  Injectable,
  computed,
  inject,
  linkedSignal,
  signal,
} from '@angular/core';
import { WINDOW } from '@tvd/cdk/browser';
import { BranchStore } from '@tvd/data-access/branch';

const STORAGE_KEY_LEFT_HAND_MODE = 'zynday:bottomBar:leftHandMode';
const STORAGE_KEY_PINNED_BRANCH = 'pinnedBranch';
const STORAGE_KEY_PINNED_ORG = 'pinnedOrg';

@Injectable({
  providedIn: 'root',
})
export class BottomBarService {
  readonly #window = inject(WINDOW);
  readonly #storage = inject(WINDOW).localStorage;
  readonly branchStore = inject(BranchStore);
  readonly branches = this.branchStore.branches;
  readonly leftHandMode = signal<boolean>(this.#loadLeftHandMode());
  readonly selectedBranchId = linkedSignal<string | null>(
    () => this.#loadSelectedBranch() ?? this.branches()[0]?.id,
  );
  readonly selectedBranch = computed(() => {
    const selectedBranchId = this.selectedBranchId();
    return this.branches().find((branch) => branch.id === selectedBranchId);
  });
  readonly selectedBranchOrgId = computed(
    () => this.selectedBranch()?.organisation,
  );

  #loadLeftHandMode(): boolean {
    if (typeof this.#window === 'undefined') {
      return false;
    }

    try {
      const stored = this.#storage.getItem(STORAGE_KEY_LEFT_HAND_MODE);
      return stored === 'true';
    } catch {
      return false;
    }
  }

  #loadSelectedBranch(): string | null {
    if (typeof this.#window === 'undefined') {
      return null;
    }

    try {
      return this.#storage.getItem(STORAGE_KEY_PINNED_BRANCH);
    } catch {
      return null;
    }
  }

  #saveToStorage(key: string, value: unknown): void {
    if (typeof this.#window === 'undefined') {
      return;
    }

    try {
      if (value === null) {
        this.#storage.removeItem(key);
      } else {
        this.#storage.setItem(key, String(value));
      }
    } catch (error) {
      console.error('Failed to save to sessionStorage:', error);
    }
  }

  toggleLeftHandMode(): void {
    this.leftHandMode.set(!this.leftHandMode());
    this.#saveToStorage(STORAGE_KEY_LEFT_HAND_MODE, this.leftHandMode());
  }

  setSelectedBranch(branchId: string | null): void {
    this.selectedBranchId.set(branchId);
    const orgId = this.selectedBranchOrgId();
    this.#saveToStorage(STORAGE_KEY_PINNED_BRANCH, branchId);
    this.#saveToStorage(STORAGE_KEY_PINNED_ORG, orgId);
  }
}
